/**
 * @description
 * Client API Keys tab component for managing a client's pool of API keys.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date January 14, 2025
 * @see admin/src/frontend/pages/ClientDetail/ClientApiKeys.test.tsx
 */

import React, { useState, useEffect, useCallback } from "react";
import { api } from "../../services/api";
import { toast } from "sonner";
import { Key, Trash2, Plus } from "lucide-react";

interface ClientApiKeysProps {
  clientId: string;
}

interface ApiKey {
  id: string;
  service: string;
  assignedToUserId: string | null;
  createdAt: string;
}

export function ClientApiKeys({ clientId }: ClientApiKeysProps) {
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [newKey, setNewKey] = useState("");
  const [service, setService] = useState("anthropic");

  const fetchKeys = useCallback(async () => {
    if (!clientId) return;
    setIsLoading(true);
    try {
      const keys = await api.getClientApiKeys(clientId);
      setKeys(keys);
    } catch {
      toast.error("Failed to fetch API keys.");
    } finally {
      setIsLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    fetchKeys();
  }, [clientId, fetchKeys]);

  const handleAddKey = async () => {
    if (!newKey.trim()) {
      toast.error("Please enter an API key.");
      return;
    }

    setIsLoading(true);
    try {
      await api.addClientApiKey(clientId, { service, apiKey: newKey });
      toast.success("API key added successfully.");
      setNewKey("");
      fetchKeys();
    } catch {
      toast.error("Failed to add API key.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteKey = async (keyId: string) => {
    if (confirm("Are you sure you want to delete this unassigned API key?")) {
      setIsLoading(true);
      try {
        await api.deleteClientApiKey(clientId, keyId);
        toast.success("API key deleted successfully.");
        fetchKeys();
      } catch {
        toast.error("Failed to delete API key.");
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-2">
        <Key className="h-5 w-5 text-muted-foreground" />
        <div>
          <h3 className="text-lg font-semibold">API Key Management</h3>
          <p className="text-sm text-muted-foreground">
            Manage the pool of API keys for this client. Keys can be assigned to
            users automatically.
          </p>
        </div>
      </div>

      {/* Add New Key Form */}
      <div className="rounded-lg border bg-card p-4">
        <h4 className="text-sm font-medium mb-3">Add New API Key</h4>
        <div className="flex gap-2">
          <input
            type="password"
            placeholder="Enter new API Key"
            value={newKey}
            onChange={(e) => setNewKey(e.target.value)}
            className="flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            disabled={isLoading}
          />
          <select
            value={service}
            onChange={(e) => setService(e.target.value)}
            className="rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            disabled={isLoading}
          >
            <option value="anthropic">Anthropic</option>
            {/* Add other services as needed */}
          </select>
          <button
            onClick={handleAddKey}
            disabled={isLoading || !newKey.trim()}
            className="flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus className="h-4 w-4" />
            {isLoading ? "Adding..." : "Add Key"}
          </button>
        </div>
      </div>

      {/* API Keys Table */}
      <div className="rounded-lg border bg-card">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="border-b bg-muted/50">
              <tr>
                <th className="p-3 text-left text-sm font-medium">Service</th>
                <th className="p-3 text-left text-sm font-medium">Status</th>
                <th className="p-3 text-left text-sm font-medium">Added On</th>
                <th className="p-3 text-right text-sm font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && keys.length === 0 ? (
                <tr>
                  <td
                    colSpan={4}
                    className="p-8 text-center text-sm text-muted-foreground"
                  >
                    <div className="flex items-center justify-center gap-2">
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent"></div>
                      Loading API keys...
                    </div>
                  </td>
                </tr>
              ) : keys.length === 0 ? (
                <tr>
                  <td
                    colSpan={4}
                    className="p-8 text-center text-sm text-muted-foreground"
                  >
                    No API keys found. Add your first key above.
                  </td>
                </tr>
              ) : (
                keys.map((key) => (
                  <tr
                    key={key.id}
                    className="border-b last:border-0 hover:bg-muted/50 transition-colors"
                  >
                    <td className="p-3 text-sm font-medium">{key.service}</td>
                    <td className="p-3 text-sm">
                      {key.assignedToUserId ? (
                        <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
                          Assigned
                        </span>
                      ) : (
                        <span className="inline-flex items-center rounded-full bg-yellow-100 px-2.5 py-0.5 text-xs font-medium text-yellow-800">
                          Unassigned
                        </span>
                      )}
                    </td>
                    <td className="p-3 text-sm text-muted-foreground">
                      {new Date(key.createdAt).toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                      })}
                    </td>
                    <td className="p-3 text-right">
                      <button
                        onClick={() => handleDeleteKey(key.id)}
                        disabled={!!key.assignedToUserId || isLoading}
                        className="inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-sm font-medium text-destructive transition-colors hover:bg-destructive/10 disabled:opacity-50 disabled:cursor-not-allowed"
                        title={
                          key.assignedToUserId
                            ? "Cannot delete assigned keys"
                            : "Delete key"
                        }
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Info Box */}
      <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
        <p className="text-sm text-blue-900">
          <strong>Note:</strong> API keys are automatically assigned to users
          when they need them. Assigned keys cannot be deleted. Only unassigned
          keys from the pool can be removed.
        </p>
      </div>
    </div>
  );
}

/*
 * © 2025 Byte Inc. All rights reserved.
 * This code is the confidential and proprietary information of Byte Inc.
 * You shall not disclose such Confidential Information and shall use it only in
 * accordance with the terms of the license agreement you entered into with Byte Inc.
 */
